/**
 * WHICH ACTES A PORTAL COUNTRY OPENS (28 Oct 2026).
 *
 * The same section serves France and, in a lighter version, Portugal: the
 * country picked in the portal's top bar decides which one, so switching
 * between them is the switch everybody already uses. Any other country has
 * no Actes.
 */
import { useQueryClient } from '@tanstack/react-query';
import { setActesCountry, currentActesCountry } from '@/integrations/actesApi';

export type ActesCountry = 'FR' | 'PT';

export const actesCountryOf = (code: string | null | undefined): ActesCountry | null =>
  code === 'FR' ? 'FR' : code === 'PT' ? 'PT' : null;

/** The tabs of the section, and the ones Portugal does without: no DPC (a
 *  French procedure) and no register of French authorities. */
export type ActesTabId = 'board' | 'dossiers' | 'demandes' | 'dpc' | 'authorities' | 'reception' | 'guide' | 'trash';
const NOT_IN_PORTUGAL: readonly ActesTabId[] = ['dpc', 'authorities'];

export function actesTabsFor<T extends { id: ActesTabId; adminOnly?: boolean }>(
  tabs: readonly T[], country: ActesCountry, me: { isSuper: boolean; isStaff: boolean } | undefined,
): T[] {
  return tabs.filter((t) => {
    if (country === 'PT' && NOT_IN_PORTUGAL.includes(t.id)) return false;
    /* The bin is the super admins' in France; Portugal's team opens its own. */
    if (t.adminOnly) return country === 'PT' ? !!me?.isStaff : !!me?.isSuper;
    return true;
  });
}

/**
 * PORTUGAL'S WORDS. «In Portugal it is not "Actes" but "Civil records", and
 * not "dossier" but "folder" — that vocabulary is France's» (Gerónimo, 28 Oct
 * 2026). The screens are shared and their English is also France's English
 * (a French user with the portal in English), so the English text is left as
 * it is and passed through this table when — and only when — the section
 * works in Portugal.
 */
const PT_WORDS: [RegExp, string][] = [
  [/\bDossiers\b/g, 'Folders'], [/\bdossiers\b/g, 'folders'],
  [/\bDossier\b/g, 'Folder'], [/\bdossier\b/g, 'folder'],
  [/\bActes\b/g, 'Civil records'],
];
export const portugalWords = (en: string): string =>
  PT_WORDS.reduce((s, [re, to]) => s.replace(re, to), en);

/** The English half of a shared Actes string, in this country's words. */
export const actesEn = (en: string): string =>
  (currentActesCountry() === 'PT' ? portugalWords(en) : en);

/** Does this dossier page show the French-only blocks (DPC, Poste log)? */
export const hasFrenchBlocks = (country: ActesCountry): boolean => country === 'FR';

/** The country an answer from the server is for — France when it does not say. */
export const countryOfRef = (ref: { me: { country?: ActesCountry } } | null | undefined): ActesCountry =>
  ref?.me.country ?? 'FR';

/**
 * Work in this country's Actes from here on. Every call then carries it, and
 * whatever was cached for the other country is dropped — the same query keys
 * serve both, so a dashboard of France must never be shown as Portugal's.
 * Called while rendering, before the first query of the screen reads the
 * cache; it only acts when the country really changes.
 */
export function useActesCountry(country: ActesCountry | null): void {
  const qc = useQueryClient();
  /* null: a screen with no Actes (another country's case) leaves it alone. */
  if (country && currentActesCountry() !== country) {
    setActesCountry(country);
    qc.removeQueries({ queryKey: ['actes'] });
  }
}

/** A dossier's status as this country reads it: France as stored (French),
 *  Portugal in English, from the same status row. */
export function dossierStatusLabel(
  ref: { me: { country?: ActesCountry }; dossierStatuses: { id: number; label_en: string }[] },
  d: { status_id: number | null; status_label_fr: string | null },
): string | null {
  if (countryOfRef(ref) === 'FR') return d.status_label_fr;
  return ref.dossierStatuses.find((s) => s.id === d.status_id)?.label_en ?? d.status_label_fr;
}

/** A type's English label — Portugal reads the shared vocabulary in English. */
export function typeLabelEn(
  types: { id: number; label_en: string; label_short_en?: string | null }[],
  id: number | null | undefined,
): string {
  const t = id == null ? undefined : types.find((x) => x.id === id);
  return t ? (t.label_short_en || t.label_en) : '';
}
